import { z } from 'zod';
/**
 * Contratos da galeria de projetos.
 *
 * Como nos demais, descrevem o JSON que trafega: datas são strings ISO. Os
 * bytes da foto nunca aparecem aqui — a imagem é servida por uma rota própria,
 * e o que trafega é a URL dela.
 */
/**
 * Teto de uma foto, em bytes.
 *
 * O navegador redimensiona e converte para WebP antes de enviar, o que põe cada
 * foto na casa de 150–250 KB. Este limite existe para quem chamar a API direto:
 * o servidor não decodifica a imagem, só recusa o que for grande demais.
 *
 * Dois megabytes também ficam confortavelmente abaixo do `max_allowed_packet`
 * de 16 MB do MySQL, que é o teto real de uma linha por consulta.
 */
export const IMAGE_MAX_BYTES = 2 * 1024 * 1024;
/** Maior lado da imagem depois do redimensionamento no navegador. */
export const IMAGE_MAX_DIMENSION = 1600;
export const projectImageSchema = z.object({
    id: z.string(),
    /** Caminho da rota que serve os bytes. */
    url: z.string(),
    alt: z.string(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    position: z.number().int().nonnegative(),
});
export const projectSchema = z.object({
    id: z.string(),
    title: z.string(),
    specs: z.string(),
    place: z.string(),
    year: z.number().int(),
    measurement: z.string().nullable(),
    position: z.number().int().nonnegative(),
    published: z.boolean(),
    images: z.array(projectImageSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
const tituloSchema = z
    .string()
    .trim()
    .min(2, 'Informe o nome do projeto.')
    .max(120, 'O nome tem no máximo 120 caracteres.');
const especificacoesSchema = z
    .string()
    .trim()
    .min(3, 'Descreva os materiais e acabamentos.')
    .max(2000, 'A descrição tem no máximo 2000 caracteres.');
const localSchema = z
    .string()
    .trim()
    .min(2, 'Informe onde a obra foi entregue.')
    .max(120, 'O local tem no máximo 120 caracteres.');
/**
 * O intervalo cobre a história da marcenaria e um ano à frente, para lançar
 * obra que está sendo entregue na virada.
 */
const anoSchema = z
    .number()
    .int()
    .min(1990, 'Ano fora do intervalo esperado.')
    .max(new Date().getFullYear() + 1, 'Ano fora do intervalo esperado.');
const medidaSchema = z.string().trim().max(40, 'A medida tem no máximo 40 caracteres.').nullable();
export const projectCreateSchema = z.object({
    title: tituloSchema,
    specs: especificacoesSchema,
    place: localSchema,
    year: anoSchema,
    measurement: medidaSchema.default(null),
    published: z.boolean().default(false),
});
export const projectUpdateSchema = z
    .object({
    title: tituloSchema.optional(),
    specs: especificacoesSchema.optional(),
    place: localSchema.optional(),
    year: anoSchema.optional(),
    measurement: medidaSchema.optional(),
    published: z.boolean().optional(),
})
    .refine((valores) => Object.values(valores).some((v) => v !== undefined), {
    message: 'Informe ao menos um campo para alterar.',
});
/**
 * Reordenação em lote.
 *
 * Arrastar um item muda a posição de vários, e mandar uma requisição por item
 * deixaria a galeria em estado inconsistente se alguma falhasse no meio.
 */
export const projectReorderSchema = z.object({
    ids: z.array(z.string()).min(1, 'Informe a nova ordem.'),
});
/** Metadados da foto. Os bytes vão no corpo da requisição, não aqui. */
export const projectImageCreateSchema = z.object({
    alt: z
        .string()
        .trim()
        .min(3, 'Descreva a imagem para quem usa leitor de tela.')
        .max(200, 'A descrição tem no máximo 200 caracteres.'),
    width: z.coerce.number().int().positive().max(10_000),
    height: z.coerce.number().int().positive().max(10_000),
});
/** Tipos aceitos. WebP é o que o navegador produz ao redimensionar. */
export const IMAGE_MIME_TYPES = ['image/webp', 'image/jpeg', 'image/png'];
export const imageMimeTypeSchema = z.enum(IMAGE_MIME_TYPES);
//# sourceMappingURL=projects.js.map